import { storeToRefs } from 'pinia'
import {
  computed,
  onScopeDispose,
  ref,
  shallowRef,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
  type ShallowRef,
} from 'vue'

import { reportViewConfigApi } from '../api/ReportViewConfigApi'
import type { ReportViewConfig, UpsertReportViewConfig } from '../models/reportViewConfig.types'
import { parseReportViewConfig, serializeReportViewConfig } from '../service/reportViewConfig'
import { useReportViewConfigStore } from '../stores/reportViewConfigStore'

import { useWorkspace } from '@/features/account'

export type ReportViewConfigsState = 'idle' | 'loading' | 'ready' | 'error'

export interface UseReportViewConfigs {
  configs: Ref<ReportViewConfig[]>
  state: Ref<ReportViewConfigsState>
  error: ShallowRef<unknown | null>
  busy: ComputedRef<boolean>
  get: (tableId: string) => ReportViewConfig | null
  /** `force` bypasses the workspace cache — a scope change already does this on its own. */
  refresh: (force?: boolean) => Promise<void>
  save: (input: Omit<UpsertReportViewConfig, 'workspaceId'>) => Promise<void>
  cancel: () => void
}

/** Existing backend transport for one saved selection per workspace + stable table id.
 *  Backed by `reportViewConfigStore`, a persisted per-workspace cache — a component remount
 *  or hard reload with the same workspace reuses the cached records instead of refetching. */
export function useReportViewConfigs(
  enabled: MaybeRefOrGetter<boolean> = true,
): UseReportViewConfigs {
  const workspace = useWorkspace()
  const cacheStore = useReportViewConfigStore()
  const { configs } = storeToRefs(cacheStore)
  const state = ref<ReportViewConfigsState>('idle')
  const error = shallowRef<unknown | null>(null)
  let controller: AbortController | null = null
  let activeRequest = 0
  let mutationTail: Promise<void> = Promise.resolve()

  const cancel = (): void => {
    activeRequest += 1
    controller?.abort()
    controller = null
  }

  const refresh = async (force = false): Promise<void> => {
    if (!toValue(enabled)) {
      cancel()
      state.value = 'idle'
      error.value = null
      return
    }
    const workspaceId = workspace.workspaceId.value
    if (workspaceId === undefined) {
      cancel()
      state.value = 'idle'
      error.value = null
      return
    }
    if (!force && cacheStore.ownsFreshWorkspace(workspaceId)) {
      state.value = 'ready'
      error.value = null
      return
    }
    controller?.abort()
    controller = new AbortController()
    const requestId = ++activeRequest
    state.value = 'loading'
    error.value = null
    try {
      const next = await reportViewConfigApi.list(workspaceId, controller.signal)
      if (requestId !== activeRequest || controller.signal.aborted) return
      cacheStore.setConfigs(workspaceId, next)
      state.value = 'ready'
    } catch (cause) {
      if (requestId !== activeRequest || controller.signal.aborted) return
      error.value = cause
      state.value = 'error'
    } finally {
      if (requestId === activeRequest) controller = null
    }
  }

  const save = (input: Omit<UpsertReportViewConfig, 'workspaceId'>): Promise<void> => {
    const requestedWorkspaceId = workspace.workspaceId.value
    if (!toValue(enabled) || requestedWorkspaceId === undefined) return Promise.resolve()
    const requestedInput = {
      ...input,
      ...(input.dimensions ? { dimensions: [...input.dimensions] } : {}),
      ...(input.metrics ? { metrics: [...input.metrics] } : {}),
      ...(Array.isArray(input.mediaColumns) ? { mediaColumns: [...input.mediaColumns] } : {}),
    }
    const operation = mutationTail.then(async () => {
      if (!toValue(enabled) || workspace.workspaceId.value !== requestedWorkspaceId) return
      controller?.abort()
      const writeController = new AbortController()
      controller = writeController
      const requestId = ++activeRequest
      state.value = 'loading'
      error.value = null
      try {
        const write = { ...requestedInput, workspaceId: requestedWorkspaceId }
        await reportViewConfigApi.upsert(write, writeController.signal)
        const normalized = parseReportViewConfig(serializeReportViewConfig(write))
        if (
          normalized &&
          requestId === activeRequest &&
          !writeController.signal.aborted &&
          workspace.workspaceId.value === requestedWorkspaceId
        ) {
          cacheStore.upsertConfig(requestedWorkspaceId, normalized)
          state.value = 'ready'
        }
      } catch (cause) {
        if (requestId !== activeRequest || writeController.signal.aborted) return
        error.value = cause
        state.value = 'error'
        throw cause
      } finally {
        if (requestId === activeRequest) controller = null
      }
    })
    mutationTail = operation.catch(() => {})
    return operation
  }

  watch(
    () => [workspace.workspaceId.value, toValue(enabled)] as const,
    () => void refresh(),
    { immediate: true },
  )
  onScopeDispose(cancel)
  return {
    configs,
    state,
    error,
    busy: computed(() => state.value === 'loading'),
    get: (tableId) => configs.value.find((item) => item.tableId === tableId) ?? null,
    refresh,
    save,
    cancel,
  }
}
